import { ImageWithFallback } from './figma/ImageWithFallback';
import { Button } from "./ui/button";

export type PageHeroProps = {
  title: string;
  subtitle: string;
  description: string;
  backgroundImage: string;
  breadcrumbs: { label: string }[];
  showCTAButtons?: boolean;
  page: string;
};

export function PageHero({ title, subtitle, description, backgroundImage, breadcrumbs, showCTAButtons = false }: PageHeroProps) {
  return (
    <section className={`relative ${showCTAButtons ? 'h-[60vh] min-h-[480px] max-h-[600px]' : 'h-[40vh] min-h-[320px] max-h-[400px]'} flex items-center justify-center overflow-hidden`}>
      {/* Background Image */}
      <div className="absolute inset-0">
        <ImageWithFallback
          src={backgroundImage}
          alt={`${title} background`}
          className="w-full h-full object-cover"
        />
        {/* Green Overlay */}
        <div 
          className="absolute inset-0 opacity-80"
          style={{ backgroundColor: '#819A40' }}
        />
        {/* Dark Gradient Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-black/10" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="mb-4">
            <ol className="flex items-center justify-center space-x-2 text-sm opacity-90">
              {breadcrumbs.map((crumb, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && <span className="mx-2 text-white/60">/</span>}
                  <span className={index === breadcrumbs.length - 1 ? 'text-white font-medium' : 'text-white/80'}>
                    {crumb.label}
                  </span>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Subtitle */}
        <p className="text-lg sm:text-xl mb-3 opacity-90 font-medium">
          {subtitle}
        </p>

        {/* Main Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
          {title}
        </h1>

        {/* Description */}
        {description && (
          <p className="text-lg sm:text-xl opacity-90 max-w-3xl mx-auto leading-relaxed mb-8">
            {description}
          </p>
        )}

        {/* CTA Buttons for ProcPay */}
        {showCTAButtons && (
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Button 
              size="lg" 
              className="text-white hover:opacity-90"
              style={{ backgroundColor: '#FF814C' }}
            >
              Book a Demo
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-black hover:bg-white hover:text-gray-900"
            >
              Download Datasheet
            </Button>
          </div>
        )}
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white/10 to-transparent" />
    </section>
  );
}